/**
 * Date formatting utilities
 */

import { MS_PER_DAY } from './constants';

const DATE_FORMATS = {
  short: { month: 'short', day: 'numeric' },
  day: { weekday: 'short', day: 'numeric' },
  dayFull: { weekday: 'long', month: 'short', day: 'numeric' },
  dayShort: { day: 'numeric' },
  weekday: { weekday: 'short' },
  weekdayLong: { weekday: 'long' },
  month: { month: 'short', year: 'numeric' },
  monthOnly: { month: 'short' },
  monthLong: { month: 'long', year: 'numeric' },
  full: { month: 'short', day: 'numeric', year: 'numeric' },
};

/**
 * Format date for display
 */
export function formatDate(date, format = 'short') {
  if (format === 'fullWithTime') {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  if (format === 'quarter') {
    const quarter = Math.floor(date.getMonth() / 3) + 1;
    return `Q${quarter} ${date.getFullYear()}`;
  }
  
  if (format === 'year') {
    return date.getFullYear().toString();
  }

  if (format === 'week') {
    const startOfYear = new Date(date.getFullYear(), 0, 1);
    const weekNum = Math.ceil(((date - startOfYear) / MS_PER_DAY + startOfYear.getDay() + 1) / 7);
    return `W${weekNum}`;
  }

  const opts = DATE_FORMATS[format] || DATE_FORMATS.short;
  return date.toLocaleDateString('en-US', opts);
}

/**
 * Check if a date is a weekend
 */
export function isWeekend(date) {
  const day = date.getDay();
  return day === 0 || day === 6;
}

/**
 * Check if a date represents the current time period
 * Used to highlight the "current" column at any zoom level
 */
export function isCurrentPeriod(date, periodType) {
  const now = new Date();
  
  switch (periodType) {
    case 'hour':
      return date.getFullYear() === now.getFullYear() &&
             date.getMonth() === now.getMonth() &&
             date.getDate() === now.getDate() &&
             date.getHours() === now.getHours();
    case 'day':
      return date.getFullYear() === now.getFullYear() &&
             date.getMonth() === now.getMonth() &&
             date.getDate() === now.getDate();
    case 'week': {
      // Check if now falls within the week starting from date
      const weekStart = new Date(date);
      const weekEnd = new Date(date);
      weekEnd.setDate(weekEnd.getDate() + 7);
      return now >= weekStart && now < weekEnd;
    }
    case 'month':
      return date.getFullYear() === now.getFullYear() &&
             date.getMonth() === now.getMonth();
    case 'quarter': {
      const dateQuarter = Math.floor(date.getMonth() / 3);
      const nowQuarter = Math.floor(now.getMonth() / 3);
      return date.getFullYear() === now.getFullYear() && dateQuarter === nowQuarter;
    }
    case 'year':
      return date.getFullYear() === now.getFullYear();
    default:
      return false;
  }
}

/**
 * Format hour for display
 */
export function formatHour(hour, detailed = false) {
  if (detailed) {
    return hour === 0 ? '12:00 AM' 
      : hour < 12 ? `${hour}:00 AM` 
      : hour === 12 ? '12:00 PM' 
      : `${hour - 12}:00 PM`;
  }
  return hour === 0 ? '12am' 
    : hour < 12 ? `${hour}am` 
    : hour === 12 ? '12pm' 
    : `${hour - 12}pm`;
}

/**
 * Format a date range for display
 * Shows month abbreviation with day numbers
 * e.g., "Jan 4 - 10" or "Dec 28 - Jan 3" (when crossing month boundary)
 */
export function formatDateRange(startDate, endDate) {
  const startMonth = startDate.toLocaleDateString('en-US', { month: 'short' });
  const endMonth = endDate.toLocaleDateString('en-US', { month: 'short' });
  const startDay = startDate.getDate();
  const endDay = endDate.getDate();
  
  // Check if crosses month boundary
  if (startDate.getMonth() !== endDate.getMonth()) {
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
  }
  return `${startMonth} ${startDay} - ${endDay}`;
}
